import { t } from '@lingui/core/macro';
import { PackageDTO } from '@luminovo/api-contracts';
import { sortBy } from '@luminovo/commons';
import { FieldSelectControlled, FieldSelectControlledProps } from '@luminovo/design-system';
import { FieldPathValue, FieldValues, Path, PathValue } from 'react-hook-form';

import { formatPackage } from '../../../formatters';

export enum UnknownPackageEnum {
    unknown = 'Unknown',
}

/** Leaves out nameless packages with a mounting or pins: on `Unknown` the user sets both and the BE creates it. */
function displayAsPackageOption(packageDto: PackageDTO): boolean {
    if (!packageDto.name && packageDto.mounting !== null) {
        return false;
    }
    if (!packageDto.name && packageDto.number_of_pins !== null) {
        return false;
    }

    return true;
}

function isUnknownOption(packageDto: PackageDTO) {
    return packageDto.name === null && packageDto.number_of_pins === null && packageDto.mounting === null;
}

export function getPackageOptions(packageDto: PackageDTO[]): PackageDTO[] {
    const filteredPackages = packageDto.filter((p) => displayAsPackageOption(p));

    return sortBy(filteredPackages, [
        (p) => {
            if (isUnknownOption(p)) {
                return 0; // forces the `Unknown` option to be first in the array
            } else {
                return 1;
            }
        },
        (p) => [p.name],
        (p) => {
            return Number(p.number_of_pins);
        },
    ]);
}

/** Usually fed with the packages from `usePartPackages`. */
export function FieldPackageControlled<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>,
    TValue extends FieldPathValue<TFieldValues, TName> & PackageDTO = PathValue<TFieldValues, TName>,
>({
    name,
    control,
    required,
    options,
}: FieldSelectControlledProps<TFieldValues, TName, TValue> & { options: PackageDTO[] }): JSX.Element {
    return (
        <FieldSelectControlled
            name={name}
            control={control}
            required={required}
            FieldProps={{
                options: getPackageOptions(options) as PathValue<TFieldValues, TName>,
                getOptionLabel: formatPackage,
                placeholder: t`Packaging`,
            }}
        />
    );
}
